/**
 * PORTED — RO-3-00f / #44473.
 * Origin: `excellent/app/projects/[projectSlug]/[sectionSlug]/_agents/views.tsx`.
 *
 * Cause C rewrite: RO-2-11 turned the `"use server"` action deps into
 * endpoints (`agents.workerSpawn`, `agentGoals.*`). This shared client body
 * consumes those plus `agentsViews.runsBootstrap` for the runs strip. Goal
 * list/detail/create mirror the shapes already proven on `/agents/goals`
 * (RO-3-02) so section mounts share one implementation.
 *
 * #44483 — NewGoalDialog (RO-3-00g cause-B port) is the ledger toolbar create
 * CTA, matching the Next `_agents/views` original (dual-kind modal when
 * `allowStrategic`, agentic-only when scope is agentic).
 */

import {
  GoalsLedger,
  PageHeader,
  StatStrip,
  type StatStripItem,
  WorkerSpawnButton,
  formatUsd,
  formatCompact,
} from "@excellent/ui";
import { useApi, apiCall } from "@excellent/ui/api";
import { RouterLink as Link } from "@excellent/ui/router";
import { agentsViews } from "@excellent/ui/api/contracts/agents-views";
import { agents } from "@excellent/ui/api/contracts/agents";
import { agentGoals } from "@excellent/ui/api/contracts/agent-goals";
import { WorkerRunsTable } from "@excellent/ui";
import { NewGoalDialog } from "../modes/new-goal-dialog";

export function AgentsRunsView({
  basePath: _basePath,
  embedded = false,
}: {
  basePath: string;
  embedded?: boolean;
}) {
  const { data, error, isLoading, mutate } = useApi(agentsViews.runsBootstrap, {});

  if (isLoading && !data) {
    return <p className="type-body text-muted-foreground">Loading runs…</p>;
  }
  if (error || !data) {
    return (
      <p className="type-body text-destructive">
        {error?.message ?? "Could not load agent runs."}
      </p>
    );
  }

  const runStats: StatStripItem[] = [
    {
      title: "Recent runs",
      value: String(data.recent.length),
      footer:
        data.counts.running > 0
          ? [{ count: data.counts.running, label: "running", intent: "info" }]
          : undefined,
      description: data.counts.running > 0 ? undefined : "Last 100 worker runs",
    },
    {
      title: "Cost today",
      value: formatUsd(data.today.cost_usd),
      description: "Spend since UTC midnight",
    },
    {
      title: "Tokens today",
      value: formatCompact(data.today.tokens),
      description: "Input + output",
    },
    {
      title: "Needs attention",
      value: String(data.counts.failed + data.counts.budgetExceeded),
      footer:
        data.counts.failed + data.counts.budgetExceeded > 0
          ? [
              ...(data.counts.failed > 0
                ? [{ count: data.counts.failed, label: "failed", intent: "danger" as const }]
                : []),
              ...(data.counts.budgetExceeded > 0
                ? [
                    {
                      count: data.counts.budgetExceeded,
                      label: "over budget",
                      intent: "warning" as const,
                    },
                  ]
                : []),
            ]
          : undefined,
      description:
        data.counts.failed + data.counts.budgetExceeded > 0
          ? undefined
          : "All healthy",
    },
  ];

  return (
    <div className="@container/page flex flex-col gap-5">
      <PageHeader
        title={embedded ? undefined : "Agents"}
        description="Worker run activity for the autonomous agent system."
        toolbar={
          <WorkerSpawnButton
            roles={data.roles}
            defaultRole="shipper"
            action={async (role) => {
              const res = await apiCall(agents.workerSpawn, { body: { role } });
              await mutate();
              return res;
            }}
          />
        }
      />
      <div className="flex flex-col gap-3">
        <StatStrip variant="table" items={runStats} />
        <WorkerRunsTable runs={data.firstPage.rows} basePath="/" />
        {data.firstPage.has_more ? (
          <p className="type-subtext text-muted-foreground">
            Showing the latest page — load more via the runs feed.
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function GoalsView({
  basePath,
  projectIds,
  scope = "all",
  embedded = false,
}: {
  basePath: string;
  projectIds?: number[];
  scope?: "all" | "agentic";
  embedded?: boolean;
}) {
  const projectIdsParam =
    projectIds && projectIds.length > 0 ? projectIds.join(",") : undefined;
  const { data, error, isLoading } = useApi(agentGoals.list, {
    query: projectIdsParam ? { projectIds: projectIdsParam } : {},
  });
  // Roles feed NewGoalDialog's agentic branch (same source the Next server
  // component used via listSpawnableRoles → agentGoals.roles endpoint).
  const { data: rolesData } = useApi(agentGoals.roles, {});

  if (isLoading && !data) {
    return <p className="type-body text-muted-foreground">Loading goals…</p>;
  }
  if (error || !data) {
    return (
      <p className="type-body text-destructive">
        {error?.message ?? "Could not load goals."}
      </p>
    );
  }

  const agentic = scope === "agentic";
  const ledgerGoals = agentic
    ? data.goals.filter((g) => g.kind === "agentic")
    : data.goals;
  const roles = (rolesData?.roles ?? []).map((r) => ({
    name: r.name,
    description: r.description ?? "",
  }));

  return (
    <div className="flex flex-col gap-5">
      {embedded ? null : <PageHeader title="Goals" />}
      {agentic ? (
        <p className="type-body text-muted-foreground">
          The fleet’s goals. All goals live in{" "}
          <Link
            href="/work/goals"
            className="underline underline-offset-2 hover:text-foreground"
          >
            Work ▸ Goals
          </Link>
          .
        </p>
      ) : null}
      <GoalsLedger
        goals={ledgerGoals}
        basePath={basePath === "/agents" ? "/" : basePath}
        toolbar={
          embedded ? undefined : (
            <NewGoalDialog roles={roles} allowStrategic={!agentic} />
          )
        }
      />
    </div>
  );
}
